import React, { useState, useRef } from "react";
import {
    FacebookShareButton,
    LinkedinShareButton,
    TwitterShareButton,
} from "react-share";
import {
    Button,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Modal,
    Box,
    CircularProgress,
    IconButton,
} from "@mui/material";
import ShareIcon from "@mui/icons-material/Share";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import XIcon from "@mui/icons-material/X";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import { QRCodeCanvas as QRCode } from "qrcode.react";
import { toPng } from "html-to-image";

import { SCHEMA_TYPE_TEXT } from '../../util/types'
import {
    isFieldForCategory,
    pickCategoryFields,
    pickCustomFieldProps,
} from "../../util/fieldHelpers";

import SectionDetailsMaybe from '../../containers/ListingPage/SectionDetailsMaybe';
import WheelplaceLogo from '../../assets/wheelplace-pos.png';


const SocialShareMenu = (props) => {

    const {
        title,
        price,
        url,
        // imageSrc,
        publicData,
        metadata,
        listingFieldConfigs,
        categoryConfiguration,
        intl,
        listing,
    } = props
    const { images } = listing || {};
    const imageSrc = images ? images[0]?.attributes?.variants?.twitter?.url : null

    const [anchorEl, setAnchorEl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const imageRef = useRef();

    const open = Boolean(anchorEl);
    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleHostClick = () => {
        setAnchorEl(null);
        setOpenModal(true);
    };

    const handleDownload = async () => {
        try {
            setLoading(true);
            const dataUrl = await toPng(imageRef.current, { cacheBust: true, skipFonts: true, });
            const link = document.createElement("a");
            link.download = `${title.replace(/\s+/g, "_")}.png`;
            link.href = dataUrl;
            link.click();
        } catch (err) {
            console.error("Error generating image", err);
        } finally {
            setLoading(false);
        }
    };
    const [localImage, setLocalImage] = useState(null);

    React.useEffect(() => {
        const fetchBase64Image = async (url) => {
            try {
                const res = await fetch(url);
                const blob = await res.blob();
                return new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.readAsDataURL(blob);
                });
            } catch (e) {
                console.error("Failed to load image", e);
                return null;
            }
        };

        if (imageSrc) {
            fetchBase64Image(imageSrc).then(setLocalImage);
        }
    }, [imageSrc]);

    if (!categoryConfiguration) {
        return null;
    }

    const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration;
    const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
    const currentCategories = Object.values(categoriesObj);

    const isFieldForSelectedCategories = fieldConfig => {
        return isFieldForCategory(currentCategories, fieldConfig);
    };

    const propsForCustomFields = pickCustomFieldProps(
        publicData,
        metadata,
        listingFieldConfigs,
        'listingType',
        isFieldForSelectedCategories
    ) || [];

    const sectionTextProps = propsForCustomFields.filter(field => field.schemaType === SCHEMA_TYPE_TEXT);


    return (
        <>
            {/* Share Button */}
            <Button
                variant="contained"
                startIcon={<ShareIcon />}
                onClick={handleClick}
                aria-controls={open ? "share-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    fontWeight: 500,
                    // px: 2,
                    // m: "12px 0 12px 20px",
                    backgroundColor: "#477bf4",
                    "&:hover": { backgroundColor: "#3366e3" },
                }}
            >
                Dela
            </Button>

            {/* Dropdown Menu */}
            <Menu
                id="share-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    elevation: 4,
                    sx: {
                        mt: 1.5,
                        minWidth: 200,
                        "& .MuiMenuItem-root": { fontSize: 14, gap: 1 },
                    },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
                <MenuItem onClick={handleClose}>
                    <FacebookShareButton
                        url={url}
                        quote={title}
                        style={{ display: "flex", alignItems: "center", width: "100%" }}
                    >
                        <ListItemIcon>
                            <FacebookIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Facebook" />
                    </FacebookShareButton>
                </MenuItem>

                <MenuItem onClick={handleClose}>
                    <LinkedinShareButton
                        url={url}
                        title={title}
                        style={{ display: "flex", alignItems: "center", width: "100%" }}
                    >
                        <ListItemIcon>
                            <LinkedInIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="LinkedIn" />
                    </LinkedinShareButton>
                </MenuItem>

                <MenuItem onClick={handleClose}>
                    <TwitterShareButton
                        url={url}
                        title={title}
                        style={{ display: "flex", alignItems: "center", width: "100%" }}
                    >
                        <ListItemIcon>
                            <XIcon />
                        </ListItemIcon>
                        <ListItemText primary="X (Twitter)" />
                    </TwitterShareButton>
                </MenuItem>

                <MenuItem onClick={handleHostClick}>
                    <ListItemIcon>
                        <CloudUploadIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Värd (Generera bild)" />
                </MenuItem>
            </Menu>

            <Modal open={openModal} onClose={() => setOpenModal(false)}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100vh",
                        flexDirection: "column",
                        backgroundColor: "rgba(0,0,0,0.6)",
                        p: 2,
                        transform: { xs: "scale(0.75, 0.8)", sm: "none" },

                    }}
                >
                    <IconButton
                        onClick={() => setOpenModal(false)}
                        sx={{
                            position: "absolute",
                            top: 20,
                            right: 20,
                            color: "#fff",
                            background: "rgba(0,0,0,0.3)",
                            "&:hover": { background: "rgba(0,0,0,0.5)" },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    <Box
                        ref={imageRef}
                        sx={{
                            width: 500,
                            backgroundColor: "#e8eef1",
                            borderRadius: 3,
                            overflow: "hidden",
                            textAlign: "center",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                            // fontFamily: "Arial, sans-serif",


                        }}
                    >
                        <Box
                            sx={{
                                width: "100%",
                                height: 270,
                                backgroundColor: "#ddd",
                                backgroundImage: `url(${localImage})`,
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                                borderRadius: 1,
                            }}
                        />

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mt: 2,
                                // mb: 1,
                                px: 1,
                                padding: '0 24px',
                                pt: { xs: 4, sm: 0 },
                            }}
                        >
                            <h3
                                style={{
                                    margin: 0, fontSize: 12, color: "#222", fontWeight: 700,
                                    background: "white", padding: " 2px 8px", borderRadius: '16px', maxWidth: '320px'
                                }}>
                                {title}</h3>
                            <h3
                                style={{
                                    margin: 0, fontSize: 12, color: "#222", fontWeight: 700,
                                    background: "white", padding: " 2px 26px", borderRadius: '16px'
                                }}>
                                {price}</h3>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                // mt: 1,
                                px: 1,
                                padding: '0 24px',
                                pt: { xs: 3, sm: 0 },
                                // height: { xs: 300, sm: "auto" },
                                height: {
                                    xs: 300,   // phones
                                    sm: 340,   // small tablets
                                    md: 350,   // medium screens
                                    lg: 350,    // large screens and up
                                }
                            }}
                        >
                            <Box sx={{ mr: 2 }}>
                                <SectionDetailsMaybe
                                    publicData={publicData}
                                    metadata={metadata}
                                    listingFieldConfigs={listingFieldConfigs}
                                    categoryConfiguration={categoryConfiguration}
                                    isFieldForCategory={isFieldForSelectedCategories}
                                    intl={intl}
                                    sectionTextProps={sectionTextProps}
                                    isForGeneration={true}

                                />
                            </Box>

                            <Box
                                sx={{
                                    flexShrink: 0,
                                    mt: 4,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 2
                                }}
                            >
                                <img
                                    src={WheelplaceLogo}
                                    alt="logo"
                                    style={{
                                        width: "120px",
                                        height: "80px",
                                        objectFit: "contain",
                                    }}
                                />

                                <QRCode value={url} size={80} />
                            </Box>

                        </Box>
                    </Box>


                    <Button
                        variant="contained"
                        startIcon={
                            loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : <DownloadIcon />
                        }
                        onClick={handleDownload}
                        sx={{
                            mt: 3,
                            backgroundColor: "#477bf4",
                            "&:hover": { backgroundColor: "#3366e3" },
                        }}
                        disabled={loading}
                    >
                        {loading ? "Laddar ned..." : "Ladda ner bild"}
                    </Button>
                </Box>
            </Modal>
        </>
    );
};

export default SocialShareMenu;
